import type { DetailedUser } from "@/user/user";

export type StAdjustmentLineKey = [number, number, string];

export const ST_ADJUSTMENTS_ACCESS = {
  hidden: "hidden",
  preview: "preview",
  enabled: "enabled",
} as const;

export type StAdjustmentsAccess =
  (typeof ST_ADJUSTMENTS_ACCESS)[keyof typeof ST_ADJUSTMENTS_ACCESS];

// A single commission/spiff line that can be pushed into ServiceTitan as a
// payroll adjustment. Fields are snake_case to match the API payload verbatim.
export interface IStAdjustmentProposal {
  // Stable identity of the earning line: [invoice_id, employee_id, category].
  key: StAdjustmentLineKey;
  invoice_id: number;
  employee_id: number;
  employee_type: string;
  activity_code_id: number | null;
  category: string;
  job_number: string | null;
  amount: number;
  amount_cents: number;
  // The text that lands on the ServiceTitan adjustment. Typed nullable because
  // the manifest column is, so the payload is allowed to carry null — not
  // because it observably does: ManifestBuilder is the only writer and has
  // defaulted a blank memo since the table was introduced.
  memo: string | null;
  posted_on: string;
  // True when this line has already been pushed to ServiceTitan for this run.
  already_pushed: boolean;
}

// One page of the per-line list. Mirrors the AP list endpoints' offset envelope
// (`items`/`has_more`/`next_page`, load-more rather than numbered pages), plus a
// `total_count` those endpoints omit — here the server has already materialized
// every proposal to build the page, so counting them is free.
export interface IStAdjustmentsPage {
  items: IStAdjustmentProposal[];
  has_more: boolean;
  next_page: number | null;
  total_count: number;
  warnings: string[];
}

export interface IPushStAdjustmentsResponse {
  status: string;
}

// ---- Summary / bulk-push (scale path) ----

// Overall rollup of a run's pushable adjustments.
export interface IStAdjustmentTotals {
  count: number;
  amount: number;
  amount_cents: number;
  pushed: number;
  pending: number;
  pushed_amount_cents: number;
  pending_amount_cents: number;
  pending_amount: number;
}

// One by-technician rollup bucket. The roster name is resolved client-side from
// `employee_id`; the tallies come straight from the API.
export interface IStAdjustmentGroup {
  employee_id?: number;
  count: number;
  amount: number;
  amount_cents: number;
  // Dollar total of just this technician's not-yet-pushed lines, for sizing a
  // per-technician push.
  pending_amount_cents: number;
  pushed: number;
  pending: number;
}

// UI model for one rollup row. The API group stays unchanged; the technician name
// is attached once in the data layer before components consume it.
export interface StAdjustmentTechnicianGroup extends IStAdjustmentGroup {
  technicianName: string | null;
}

// The exact scope and totals staged for push confirmation.
export interface StAdjustmentPushIntent {
  // Technician ids to push, or null for every pending line in the run.
  technicianIds: number[] | null;
  lineCount: number;
  amountCents: number;
  // Roster name for a single-technician push when one is available.
  technicianName?: string | null;
}

export type StAdjustmentPushStatus =
  | "idle"
  | "queued"
  | "running"
  | "completed"
  // The push job crashed before completing; terminal, so a new push is allowed.
  | "failed";

export interface IStAdjustmentPushFailure {
  line_key: StAdjustmentLineKey;
  error: string;
}

// Live progress of a bulk "push all", polled while a batch runs.
export interface IStAdjustmentPushProgress {
  status: StAdjustmentPushStatus;
  total: number;
  pushed: number;
  skipped: number;
  failed: number;
  failures: IStAdjustmentPushFailure[];
  started_at?: string;
  finished_at?: string;
  // Set only when status is "failed": the message from the job that crashed.
  error?: string;
}

export interface IStAdjustmentsSummaryResponse {
  totals: IStAdjustmentTotals;
  by_technician: IStAdjustmentGroup[];
  warnings: string[];
  push_status: IStAdjustmentPushProgress;
}

export interface StAdjustmentsViewSummary
  extends Omit<IStAdjustmentsSummaryResponse, "by_technician"> {
  by_technician: StAdjustmentTechnicianGroup[];
}

export interface StAdjustmentsPreviewLine {
  jobNumber: string;
  amountCents: number;
  category: string;
  memo: string;
}

export type StAdjustmentsPreviewUser = Pick<
  DetailedUser,
  "first_name" | "last_name" | "crm_external_id" | "role" | "discarded_at"
>;
